import { defineStore } from 'pinia';
import { ref } from 'vue';

export interface Toast {
    id: string;
    message: string;
    type?: 'success' | 'error' | 'warning' | 'info';
    duration?: number; // in milliseconds
    action?: {
        text: string;
        callback: () => void;
    };
}

export const useToastStore = defineStore('toast', () => {
    const toasts = ref<Toast[]>([]);

    function addToast(toast: Omit<Toast, 'id'>) {
        const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
        const duration = toast.duration || 5000; // default 5 seconds
        
        const newToast: Toast = {
            id,
            ...toast,
            duration,
        };

        toasts.value.push(newToast);

        // Auto-dismiss after duration
        setTimeout(() => {
            removeToast(id);
        }, duration);

        return id;
    }

    function removeToast(id: string) {
        const index = toasts.value.findIndex((toast) => toast.id === id);
        if (index > -1) {
            toasts.value.splice(index, 1);
        }
    }

    function clearAllToasts() {
        toasts.value = [];
    }

    // Convenience methods for different toast types
    function showSuccess(message: string, duration?: number, action?: Toast['action']) {
        return addToast({
            message, type: 'success', duration, action, 
        });
    }

    function showError(message: string, duration?: number, action?: Toast['action']) {
        return addToast({
            message, type: 'error', duration, action, 
        });
    }

    function showWarning(message: string, duration?: number, action?: Toast['action']) {
        return addToast({
            message, type: 'warning', duration, action, 
        });
    }

    function showInfo(message: string, duration?: number, action?: Toast['action']) {
        return addToast({
            message, type: 'info', duration, action, 
        });
    }

    return {
        toasts,
        addToast,
        removeToast,
        clearAllToasts,
        showSuccess,
        showError,
        showWarning,
        showInfo,
    };
}); 
